import { trackButtonClicked } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { Button } from '@heidi/design-system/button';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { Loader2, Plus as PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

interface AddToLibraryButtonProps {
  templateId?: string;
  templateAlreadyExists: boolean;
  className?: string;
  host: {
    hasUser: boolean;
    isAuthenticated: boolean;
    tutorialStepCurrentPage?: { enum?: string };
    panelOpen: boolean;
    isPending: boolean;
    importTemplateFromGallery: (templateId: string) => void;
    onUnauthenticatedClick: (templateId: string) => void;
  };
}

export const AddToLibraryButton = ({
  templateId,
  templateAlreadyExists,
  className,
  host,
}: AddToLibraryButtonProps) => {
  const {
    hasUser,
    isAuthenticated,
    tutorialStepCurrentPage,
    panelOpen,
    isPending,
    importTemplateFromGallery,
    onUnauthenticatedClick,
  } = host;

  const shouldHighlightAddToLibrary = useMemo(
    () =>
      tutorialStepCurrentPage?.enum === TemplatePages.ADD_TO_YOUR_LIBRARY && panelOpen,
    [panelOpen, tutorialStepCurrentPage?.enum]
  );

  const handleClick = () => {
    if (!templateId) {
      return;
    }
    trackButtonClicked({
      screenName: ScreenName.TemplateCommunityDetails,
      action: ButtonAction.CommunityTemplateImport,
      label: isAuthenticated ? 'Add to my library' : 'Use Template',
    });
    if (isAuthenticated) {
      importTemplateFromGallery(templateId);
      return;
    }
    onUnauthenticatedClick(templateId);
  };

  return (
    <Button
      data-testid="community-add-to-library-button"
      size="md"
      className={cn(
        // Long translations wrap rather than clip in the 192px sidebar
        'h-auto min-h-9 whitespace-normal py-2 text-start leading-tight',
        shouldHighlightAddToLibrary && 'animation-ping',
        className
      )}
      onClick={handleClick}
      disabled={templateAlreadyExists || (isAuthenticated && isPending)}
    >
      {isAuthenticated && isPending ? <Loader2 className="animate-spin" /> : <PlusIcon />}
      {templateAlreadyExists ? (
        <FormattedMessage
          defaultMessage="Already in your library"
          id="+geA9TIYUn"
          description="Button label when a community template is already in the user’s library"
        />
      ) : hasUser ? (
        <FormattedMessage
          defaultMessage="Add to my library"
          id="4e6MHpbUaF"
          description="Button label for adding a community template to the user’s library"
        />
      ) : (
        <FormattedMessage
          defaultMessage="Use template"
          id="VM1qiCYtr3"
          description="Button label for signing in to use a community template"
        />
      )}
    </Button>
  );
};
